/**
 * GS Web Intelligence SDK — public entry + session orchestrator.
 *
 * Usage (CDN / IIFE):
 *   <script src="https://cdn.gammasweep.com/gs.js"></script>
 *   <script>
 *     gs.config({ gps: "prompt" });
 *     const token = await gs.getSession();   // opaque JWE, send to your backend
 *   </script>
 *
 * The orchestrator runs every collector in parallel under a hard wall-clock
 * budget (`max_wait_time`), assembles the `Signals` envelope, and seals it into
 * a compact JWE. Nothing identifying ever leaves the page in cleartext (except
 * in explicit `sandbox` mode for `gk_test_` integration).
 */

import type {
  BehaviorSignals, EmailHintSignals, GpsSignals, NetworkSignals,
  GsConfig, Signals, TokenPayload, DeviceSignals,
} from "./types.ts";
import { sealJwe } from "./crypto/jwe.ts";
import { ACTIVE_KID } from "./keys.ts";
import { uuid, randomHex } from "./util/sha256.ts";
import { safe, now } from "./util/async.ts";
import { collectDevice } from "./collectors/device.ts";
import { collectTamper, spoofingHash } from "./collectors/tamper.ts";
import { collectBrowser } from "./collectors/browser.ts";
import { collectStorage } from "./collectors/storage.ts";
import { collectGps } from "./collectors/gps.ts";
import { collectNetwork } from "./collectors/network.ts";
import { startBehaviorCapture, stopBehaviorCapture, snapshotBehavior } from "./collectors/behavior.ts";

const TTL_MS = 5 * 60 * 1000; // must mirror _shared/device-token.ts
const GPS_PROMPT_TIMEOUT_MS = 15_000;

const defaults: Required<Omit<GsConfig, "session_id">> = {
  gps: "prompt",
  behavior: true,
  persistence: true,
  max_wait_time: 4000,
  sandbox: false,
  debug: false,
};

let cfg: GsConfig = {};
let lastGpsSignals: GpsSignals | null = null;

function log(...args: unknown[]): void {
  if (cfg.debug) console.info("[gs]", ...args);
}

/* ---- placeholder signal blocks (behavior + email filled in P5/host) ---- */
function emptyGps(): GpsSignals {
  return { latitude: null, longitude: null, accuracy_m: null, permission_state: null };
}
function emptyNetwork(): NetworkSignals {
  return {
    effective_type: null, rtt: null, downlink: null, save_data: null,
    webrtc_local_ips: [], public_ip_hint: null, dns_resolver_region: null,
  };
}
function emptyBehavior(): BehaviorSignals {
  return {
    mouse_path_entropy: null, keystroke_dwell_avg_ms: null, keystroke_flight_avg_ms: null,
    scroll_cadence_ms: null, form_fill_speed_cps: null, copy_paste_used: null,
    tab_switches: null, session_duration_ms: null, risk_score: null,
  };
}
function emptyEmail(address: string | null): EmailHintSignals {
  return { address };
}


/** Collect every available signal under the configured time budget. */
async function collectSignals(): Promise<Signals> {
  const persist = cfg.persistence ?? defaults.persistence;
  const budget = cfg.max_wait_time ?? defaults.max_wait_time;
  const gpsMode = cfg.gps ?? defaults.gps;
  const gpsBudget = gpsMode === "prompt" || gpsMode === "required"
    ? Math.max(budget, GPS_PROMPT_TIMEOUT_MS)
    : Math.min(budget, 3000);

  // Network + GPS probes are I/O-bound (WebRTC, geolocation) and must share the
  // same wall-clock budget as the synchronous collectors — run everything flat.
  const t0 = now();
  const [device, tamper, browser, network, gps] = await Promise.all([
    safe(collectDevice(persist), fallbackDevice(), budget),
    safe(collectTamper(), fallbackTamper(), budget),
    safe(collectBrowser(), fallbackBrowser(), budget),
    safe(collectNetwork(Math.min(budget, 2500)), emptyNetwork(), budget),
    safe(collectGps(gpsMode, gpsBudget), emptyGps(), gpsBudget + 250),
  ]);
  lastGpsSignals = gps;

  // Merge tamper posture into the device's spoofing_hash.
  device.spoofing_hash = await safe(spoofingHash(tamper), null, 500);

  // Storage depends on whether the persistent id pre-existed (device read it).
  const idPresent =
    (device as unknown as { __persistent_id_present?: boolean }).__persistent_id_present ?? null;
  const storage = await safe(collectStorage(idPresent), fallbackStorage(), budget);
  delete (device as unknown as Record<string, unknown>).__persistent_id_present;

  log(`signals collected in ${Math.round(now() - t0)}ms`);

  return {
    device,
    browser,
    tamper,
    gps,
    network,
    storage,
    behavior: (cfg.behavior ?? defaults.behavior) ? snapshotBehavior() : emptyBehavior(),
    email: emptyEmail(null),
  };
}


/* ---- minimal fallbacks if a whole collector times out ---- */
function fallbackDevice(): DeviceSignals {
  return {
    device_hash: null, true_device_id: uuid(), browser_hash: null, cookie_hash: null,
    canvas_hash: null, webgl_hash: null, webgl2_hash: null, webgl_image_hash: null,
    webgl_params_hash: null, audio_hash: null, font_list_hash: null, spoofing_hash: null,
    screen: { width: null, height: null, color_depth: null, pixel_ratio: null, refresh_rate: null, is_extended: null },
    touch_support: null, device_memory: null, hardware_concurrency: null,
    platform: null, vendor: null, timezone: null, timezone_offset: null,
    languages: null, do_not_track: null, gpc: null,
  };
}
function fallbackTamper() {
  return {
    is_webdriver: null, is_headless: null, is_emulator: null, function_tampered: null,
    ua_platform_mismatch: null, private_mode: null, automation_flags: [],
    screen_mirrored: null, remote_desktop_suspected: null,
  };
}
function fallbackBrowser() {
  return {
    user_agent: null, browser_name: null, browser_version: null, version_age_years: null,
    version_age_bucket: null, privacy_extension_detected: null, is_bot: null,
    spoofing_detected: null, dynamic_component_blocked: null,
  };
}
function fallbackStorage() {
  return {
    cookies_enabled: null, local_storage: null, session_storage: null, indexed_db: null,
    cache_api: null, quota_bytes: null, usage_bytes: null, persistent_id_present: null,
  };
}

/* ----------------------------- public API ----------------------------- */
export function config(opts: GsConfig): void {
  cfg = { ...cfg, ...opts };
  log("configured", cfg);
  // Always-on behavioral capture begins at config time so dynamics accumulate
  // across the full page lifetime (and persist across multiple getSession calls).
  if (cfg.behavior ?? defaults.behavior) {
    startBehaviorCapture();
  } else {
    stopBehaviorCapture();
  }
}


/**
 * Build a fresh, single-use session token. Generate a new one per protected
 * user-action (login, checkout, signup) — tokens are one-time and expire in 5m.
 */
export async function getSession(): Promise<string> {
  const iat = Date.now();
  const signals = await collectSignals();
  if ((cfg.gps ?? defaults.gps) === "required" && (signals.gps.latitude == null || signals.gps.longitude == null)) {
    const err = new Error("GPS location is required but was not captured. Allow location permission and retry.") as Error & {
      code?: string;
      gps_permission_state?: GpsSignals["permission_state"];
    };
    err.name = "GpsRequiredError";
    err.code = "GPS_REQUIRED";
    err.gps_permission_state = signals.gps.permission_state;
    throw err;
  }
  const payload: TokenPayload = {
    v: 1,
    sid: cfg.session_id ?? uuid(),
    iat,
    exp: iat + TTL_MS,
    nonce: randomHex(16),
    collected_at: iat,
    signals,
  };

  if (cfg.sandbox ?? defaults.sandbox) {
    log("sandbox mode — returning unencrypted payload");
    return `gsds_sandbox.${btoa(JSON.stringify(payload)).replace(/=+$/, "")}`;
  }

  return await sealJwe(payload, ACTIVE_KID);
}

/** Convenience: only the persistent device identity, without a full token. */
export async function getDeviceId(): Promise<string> {
  const device = await safe(collectDevice(cfg.persistence ?? defaults.persistence), fallbackDevice(), 3000);
  return device.true_device_id ?? uuid();
}

/** Last GPS block captured by getSession(); useful for integration diagnostics. */
export function getLastGps(): GpsSignals | null {
  return lastGpsSignals ? { ...lastGpsSignals } : null;
}

export const gs = { config, getSession, getDeviceId, getLastGps };

// Attach to window for the IIFE/CDN build.
if (typeof window !== "undefined") {
  (window as unknown as { gs?: typeof gs }).gs = gs;
}

export type { GsConfig, Signals, TokenPayload };
